/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { FC, useEffect, useState } from "react"
import { TrashCanFilled } from "@duxton-web/icons"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"
import webPImageCache from "utils/webPImageCache"

import { ImageWithFetchRetry } from "./ImageWithFetchRetry"

const getImageStyle = ({
  width,
  height,
  opacity,
}: {
  width: number
  height: number
  opacity?: number
}) => css`
  object-fit: cover;
  width: ${width}px;
  height: ${height}px;
  border-radius: 8px;
  opacity: ${opacity ?? 1};
`

const noImageStyle = css`
  font-size: 14px;
  line-height: 20px;
  background: #efefef;
  text-align: center;
  padding: 16px;
  color: #c6c6c6;
`

const style = css`
  position: relative;
  .delete {
    color: red;
    display: flex;
    position: absolute;
    bottom: 0;
    right: 0;
    background-color: #fff;
    height: 25px;
    width: 25px;
    border-radius: 50%;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }
  &:not(:hover) .delete {
    display: none;
  }
`

export interface IImage {
  src?: any
  alt?: string
  width: number
  height: number
  opacity?: number
  showDelete?: boolean
  onClickDelete?(): any
}

export const Image: FC<IImage> = ({
  src,
  alt,
  width,
  height,
  opacity,
  showDelete = false,
  onClickDelete,
}: IImage) => {
  const imageStyle = getImageStyle({ width, height, opacity })

  return (
    <div css={style}>
      {src ? (
        <ImageWithFetchRetry
          src={src}
          width={width}
          height={height}
          alt={alt}
          css={imageStyle}
        />
      ) : (
        <div
          css={css`
            ${imageStyle}
            ${noImageStyle}
          `}
        >
          No Photo
        </div>
      )}

      {showDelete && (
        <div
          className="delete"
          onClick={() => {
            onClickDelete?.()
          }}
        >
          <TrashCanFilled />
        </div>
      )}
    </div>
  )
}

interface IWebPImage {
  webPSrc?: string
  defaultSrc?: string
  alt?: string
  width: number
  height: number
  opacity?: number
}

export const WebPImage: FC<IWebPImage> = ({
  webPSrc,
  defaultSrc,
  alt,
  width,
  height,
  opacity,
}: IWebPImage) => {
  const [webPFailed, setWebPFailed] = useState(false)

  useEffect(() => {
    setWebPFailed(!!webPSrc && webPImageCache.isImageFailed(webPSrc))
  }, [webPSrc])

  const enablePortalWebp = getGrabXValueFromLocalStorage<boolean>(
    MerchantPortalFeatureFlag.enablePortalWebp,
  )

  if (!defaultSrc || !enablePortalWebp) {
    return (
      <Image
        src={defaultSrc}
        alt={alt}
        width={width}
        height={height}
        opacity={opacity}
      />
    )
  }

  const enableWebPSource = !!webPSrc && !webPFailed
  const imageStyle = getImageStyle({ width, height, opacity })

  const handleImageError = () => {
    if (enableWebPSource) {
      // WebP failed, mark it as failed and add to cache
      setWebPFailed(true)
      webPImageCache.addFailedImage(webPSrc)
    }
  }

  return (
    <picture>
      {enableWebPSource && <source srcSet={webPSrc} type="image/webp" />}
      <img
        src={defaultSrc}
        alt={alt}
        onError={handleImageError}
        css={imageStyle}
      />
    </picture>
  )
}
